import { useEffect, useRef } from "react";
import { fromLonLat } from "ol/proj";

export const useMapCameraAnimation = ({
  map,
  edit,
  cameraState,
  routeSplit,
  prefersReducedMotion,
}) => {
  const targetCameraRef = useRef(null);
  const rafRef = useRef(null);
  const isFirstCameraRenderRef = useRef(true);

  useEffect(() => {
    if (!map || edit) return;

    const view = map.getView();
    if (!view) return;

    const targetCenter = routeSplit?.cometCoord || cameraState?.centerLonLat;
    if (!targetCenter) return;

    const targetCenterProjected = fromLonLat(targetCenter);
    const targetZoom = cameraState?.zoom;

    const isTest =
      typeof process !== "undefined" && process.env?.NODE_ENV === "test";
    if (isTest || isFirstCameraRenderRef.current || prefersReducedMotion) {
      isFirstCameraRenderRef.current = false;
      view.setCenter(targetCenterProjected);
      view.setZoom(targetZoom);
      return;
    }

    targetCameraRef.current = {
      center: targetCenterProjected,
      zoom: targetZoom,
    };

    if (rafRef.current) return;

    const animate = () => {
      if (!targetCameraRef.current) {
        rafRef.current = null;
        return;
      }

      const currentCenter = view.getCenter();
      const currentZoom = view.getZoom();
      const target = targetCameraRef.current;

      if (!currentCenter || currentZoom === undefined) {
        view.setCenter(target.center);
        view.setZoom(target.zoom);
        rafRef.current = null;
        return;
      }

      const lerpFactor = 0.22;
      const dx = target.center[0] - currentCenter[0];
      const dy = target.center[1] - currentCenter[1];
      const dz = target.zoom - currentZoom;

      const dist = Math.hypot(dx, dy);
      const absDz = Math.abs(dz);

      if (dist < 0.5 && absDz < 0.002) {
        view.setCenter(target.center);
        view.setZoom(target.zoom);
        rafRef.current = null;
        return;
      }

      view.setCenter([
        currentCenter[0] + dx * lerpFactor,
        currentCenter[1] + dy * lerpFactor,
      ]);
      view.setZoom(currentZoom + dz * lerpFactor);

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [map, cameraState, routeSplit, edit, prefersReducedMotion]);
};
